/**
 * Wire protocol shared by the client and the relay server.
 *
 * Two channels over one WebSocket:
 *   - Text frames carry JSON control messages (lobby, start, errors). These are
 *     rare, so readability beats compactness.
 *   - Binary frames carry the per-turn command traffic. This runs 20 times a
 *     second for the whole match, so it is packed by hand.
 *
 * The relay never inspects command payloads. It only guarantees that every
 * client receives the same commands for the same turn, in the same order.
 */

export const BIN_COMMANDS = 0x02;
export const BIN_TURN = 0x84;

export interface MatchSettings {
  seedLo: number;
  seedHi: number;
  mapWidth: number;
  mapHeight: number;
  playerCount: number;
  /** How many turns ahead commands are scheduled, to cover network latency. */
  commandDelay: number;
}

export interface LobbyPlayer {
  name: string;
  ready: boolean;
}

export type ClientMessage =
  | { t: 'join'; room: string; name: string }
  | { t: 'ready'; ready: boolean }
  | { t: 'chat'; text: string };

export type ServerMessage =
  | { t: 'welcome'; playerIndex: number; room: string; players: LobbyPlayer[] }
  | { t: 'lobby'; players: LobbyPlayer[] }
  | { t: 'start'; settings: MatchSettings; playerIndex: number }
  | { t: 'chat'; player: number; text: string }
  | { t: 'playerLeft'; player: number; name: string }
  | { t: 'desync'; turn: number; checksums: string[] }
  | { t: 'error'; message: string };

/**
 * Client -> server: this player's commands and state hash for `turn`.
 * Layout: [op u8][turn u32][checksum u64][command bytes...]
 */
export function encodeCommands(
  turn: number,
  commands: Uint8Array,
  checksum: bigint,
): ArrayBuffer {
  const buf = new ArrayBuffer(13 + commands.length);
  const view = new DataView(buf);
  view.setUint8(0, BIN_COMMANDS);
  view.setUint32(1, turn, true);
  view.setBigUint64(5, checksum, true);
  new Uint8Array(buf, 13).set(commands);
  return buf;
}

export interface DecodedCommands {
  turn: number;
  checksum: bigint;
  commands: Uint8Array;
}

export function decodeCommands(buf: ArrayBuffer): DecodedCommands | null {
  if (buf.byteLength < 13) return null;
  const view = new DataView(buf);
  if (view.getUint8(0) !== BIN_COMMANDS) return null;
  return {
    turn: view.getUint32(1, true),
    checksum: view.getBigUint64(5, true),
    commands: new Uint8Array(buf, 13),
  };
}

/**
 * Server -> client: every player's commands for one turn.
 * Layout: [op u8][turn u32][count u8]{[player u8][len u16][bytes]}*
 */
export function encodeTurn(turn: number, perPlayer: Uint8Array[]): ArrayBuffer {
  let size = 6;
  for (const c of perPlayer) size += 3 + c.length;
  const buf = new ArrayBuffer(size);
  const view = new DataView(buf);
  const bytes = new Uint8Array(buf);
  view.setUint8(0, BIN_TURN);
  view.setUint32(1, turn, true);
  view.setUint8(5, perPlayer.length);
  let off = 6;
  for (let p = 0; p < perPlayer.length; p++) {
    view.setUint8(off, p);
    view.setUint16(off + 1, perPlayer[p].length, true);
    bytes.set(perPlayer[p], off + 3);
    off += 3 + perPlayer[p].length;
  }
  return buf;
}

export interface DecodedTurn {
  turn: number;
  /** Commands indexed by player slot. */
  perPlayer: Uint8Array[];
}

export function decodeTurn(buf: ArrayBuffer): DecodedTurn | null {
  if (buf.byteLength < 6) return null;
  const view = new DataView(buf);
  if (view.getUint8(0) !== BIN_TURN) return null;
  const turn = view.getUint32(1, true);
  const count = view.getUint8(5);
  const perPlayer: Uint8Array[] = [];
  let off = 6;
  for (let i = 0; i < count; i++) {
    // A truncated frame must not produce a half-applied turn: bail out whole.
    if (off + 3 > buf.byteLength) return null;
    const player = view.getUint8(off);
    const len = view.getUint16(off + 1, true);
    if (off + 3 + len > buf.byteLength) return null;
    perPlayer[player] = new Uint8Array(buf.slice(off + 3, off + 3 + len));
    off += 3 + len;
  }
  for (let i = 0; i < count; i++) {
    if (!perPlayer[i]) perPlayer[i] = new Uint8Array(0);
  }
  return { turn, perPlayer };
}

/** Concatenates each player's command bytes into the single buffer the sim takes. */
export function mergeTurnCommands(perPlayer: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const c of perPlayer) total += c.length;
  const out = new Uint8Array(total);
  let off = 0;
  // Player order is fixed, so every client feeds the simulation the same bytes.
  for (const c of perPlayer) {
    out.set(c, off);
    off += c.length;
  }
  return out;
}
